/**
 * Plan & billing (firm-model spec §7, §10.2 Plan & billing page; stage A2). Declared once
 * here; the server answers with these and the web panel parses them. Money is Paddle's:
 * minor units as a string plus an ISO currency code.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";

export type BillingCycle = "monthly" | "annual";
export type SellablePlan = "tier1" | "team";

export type Money = { amount: string; currency: string };

const money = z.object({ amount: z.string(), currency: z.string() });
const cycle = z.enum(["monthly", "annual"]);

/** Catalog prices per cycle, for the upgrade and seat screens (null when not in the catalog). */
export type PlanPrices = { monthly: Money | null; annual: Money | null };

const planPrices = z.object({ monthly: money.nullable(), annual: money.nullable() });

/** `GET /v1/billing` (manage_billing). */
export type BillingOverview = {
  plan: string;
  /** Null without a live subscription (trial, beta, cancelled). */
  cycle: BillingCycle | null;
  subscribed: boolean;
  renewsAt: string | null;
  cancelsAt: string | null;
  /** Seats Team's base includes (5); the seat stepper never goes below it. */
  includedSeats: number;
  prices: { individual: PlanPrices; teamBase: PlanPrices; teamSeat: PlanPrices };
  /** Monthly AI credits from the plans table: Individual's, and Team's per seat (the pool
   * is that × seats). Null = the plan is uncapped. */
  credits: { individual: number | null; teamPerSeat: number | null };
  /** What the live subscription itself charges, which the plan card shows: a grandfathered
   * Individual pays its old price, not the catalog's (Dan 2026-09-29). `seat` is null
   * while a Team firm has no extra seats. Null without a live subscription. */
  paying: { base: Money | null; seat: Money | null } | null;
};

export const billingOverviewSchema = z.object({
  plan: z.string(),
  cycle: cycle.nullable(),
  subscribed: z.boolean(),
  renewsAt: z.string().nullable(),
  cancelsAt: z.string().nullable(),
  includedSeats: z.number().int(),
  prices: z.object({ individual: planPrices, teamBase: planPrices, teamSeat: planPrices }),
  credits: z.object({ individual: z.number().int().nullable(), teamPerSeat: z.number().int().nullable() }),
  paying: z.object({ base: money.nullable(), seat: money.nullable() }).nullable(),
});

expectParity<z.infer<typeof billingOverviewSchema>, BillingOverview>(true);

/** `POST /v1/billing/checkout`: buy a plan, or upgrade Individual → Team on the same
 * subscription (R16). `seats` is Team's seat count (≥ the included seats). */
export type CheckoutRequest = { planId: SellablePlan; cycle: BillingCycle; seats?: number };

export const checkoutRequestSchema = z.object({
  planId: z.enum(["tier1", "team"]),
  cycle,
  seats: z.number().int().min(1).max(1000).optional(),
});

expectParity<z.infer<typeof checkoutRequestSchema>, CheckoutRequest>(true);

/** A new subscription opens Paddle checkout (the overlay by transaction id, or the hosted
 * URL); an upgrade of a live subscription is applied at once. */
export type CheckoutResponse =
  | { kind: "checkout"; transactionId: string; url: string | null }
  | { kind: "upgraded" };

export const checkoutResponseSchema = z.union([
  z.object({ kind: z.literal("checkout"), transactionId: z.string(), url: z.string().nullable() }),
  z.object({ kind: z.literal("upgraded") }),
]);

expectParity<z.infer<typeof checkoutResponseSchema>, CheckoutResponse>(true);

/** `POST /v1/billing/seats` and `/v1/billing/seats/preview`. */
type SeatChangeRequest = { seats: number };

export const seatChangeRequestSchema = z.object({ seats: z.number().int().min(1).max(1000) });

expectParity<z.infer<typeof seatChangeRequestSchema>, SeatChangeRequest>(true);

/** What a seat change costs. Every change applies at once and is prorated (Dan
 * 2026-09-29): adding charges the rest of the period now, removing credits it (Paddle keeps
 * the credit and applies it to the next invoices). */
export type SeatPreview = {
  seats: number;
  /** The prorated result now: a charge or a credit; null when Paddle reports none. */
  prorated: { action: "charge" | "credit"; amount: Money } | null;
  /** The recurring charge after the change, per billing period. */
  recurring: Money | null;
};

export const seatPreviewSchema = z.object({
  seats: z.number().int(),
  prorated: z.object({ action: z.enum(["charge", "credit"]), amount: money }).nullable(),
  recurring: money.nullable(),
});

expectParity<z.infer<typeof seatPreviewSchema>, SeatPreview>(true);
